/**
 * **C04 · The year-end report to the directors** (Stream C, Documents.dc.html): from the finance
 * director, in December — the four figures (the one-pager's, read as the AOD package reads them),
 * the distributions as the two names voted them (the plan's snapshot), the year's 8350 projects
 * and their vote, and the finance year's rows done and not. Only the first of its four pages is
 * drawn, so only it is built. This lays the figures out; it computes none of them.
 */
import { type AodYear } from './aod_package.js';
import { NO_DATA, type PaperBlock, type PaperCell, type PaperPage } from './paper_page.js';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const parts = (ymd: string): [number, number, number] | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
};
/** `2026-11-04` → `Nov 4`. */
export const shortDay = (ymd: string): string => {
  const p = parts(ymd);
  return p ? `${MONTHS[p[1] - 1] ?? ''} ${String(p[2])}` : NO_DATA;
};
/** `2026-10-05` → `October 5, 2026`. */
export const longDay = (ymd: string): string => {
  const p = parts(ymd);
  return p ? `${LONG[p[1] - 1] ?? ''} ${String(p[2])}, ${String(p[0])}` : NO_DATA;
};

/** A distribution plan step as the report prints it. */
export interface YearEndStep {
  label: string;
  cents: number;
  /** archdiocese · share (paid out) · held (a holdback kept, the seed). */
  kind: 'archdiocese' | 'share' | 'held';
  /**
   * The Detroit day its bill's check printed (Money, M18); null while it is still owed; absent
   * when Money has not said — the column then reads NO DATA.
   */
  paidOn?: string | null;
}

export interface YearEndInput {
  fiscalYear: number;
  /** The day the report is prepared (YYYY-MM-DD, Detroit). */
  day: string;
  /** The one-pager's year; null when it did not answer. */
  year: AodYear | null;
  /** The distribution plan; null when there is none. `voted` is the second approval's day. */
  plan: { voted: string | null; steps: YearEndStep[]; aodBps: number | null } | null;
  /** The 8350 projects the year's close-out funds; null when Money did not answer. */
  projects: { name: string; amountCents: number; state: string; voteOn: string | null }[] | null;
  /** The finance year's rows; null when Money did not answer. */
  finance: { obligation: string; due: string | null; state: string }[] | null;
  financeDirector: string | null;
}

const fig = (c: number | null | undefined): PaperCell => (typeof c === 'number' ? c : NO_DATA);

function distributions(i: YearEndInput): PaperBlock {
  const p = i.plan;
  const head = p?.voted
    ? `DISTRIBUTIONS · AS VOTED ${shortDay(p.voted).toUpperCase()}`
    : 'DISTRIBUTIONS · NOT YET VOTED';
  if (!p) return { head, cols: '1fr 140px 120px', rows: [[NO_DATA, '', '']] };
  const rate = p.aodBps === null ? '' : ` · ${String(p.aodBps / 100)}%`;
  const order = { archdiocese: 0, share: 1, held: 2 };
  return {
    head,
    cols: '1fr 140px 120px',
    rows: [...p.steps]
      .filter((s) => s.cents !== 0 || s.kind === 'share')
      .sort((a, b) => order[a.kind] - order[b.kind])
      .map((s) => [
        s.kind === 'archdiocese' ? `${s.label}${rate}` : s.label,
        s.cents,
        // When each was paid is Money's to say (M18); a holdback is kept, not paid.
        s.kind === 'held'
          ? 'held'
          : !p.voted
            ? 'not yet voted'
            : s.paidOn === undefined
              ? NO_DATA
              : s.paidOn === null
                ? 'owed'
                : `paid ${shortDay(s.paidOn)}`,
      ]),
  };
}

function projects(i: YearEndInput): PaperBlock {
  const ps = i.projects;
  const waiting = ps?.length && ps.every((p) => p.state === 'proposed');
  const status = (p: NonNullable<YearEndInput['projects']>[number]): string => {
    const on = p.voteOn ? ` · ${shortDay(p.voteOn)}` : '';
    if (p.state === 'proposed') return `awaiting the vote${on}`;
    if (p.state === 'approved') return `voted yes${on}`;
    return `${p.state}${on}`;
  };
  return {
    head: `PROJECTS · 8350${waiting ? ' · AWAITING THE DIRECTORS’ VOTE' : ''}`,
    cols: '1fr 120px 160px',
    rows: !ps
      ? [[NO_DATA, '', '']]
      : ps.length
        ? ps.map((p) => [p.name, p.amountCents, status(p)])
        : [['No project this year', '', '']],
  };
}

function financeYear(i: YearEndInput): PaperBlock {
  const f = i.finance;
  if (!f) return { head: 'THE FINANCE YEAR', cols: '1fr 120px', rows: [[NO_DATA, '']] };
  // The rows due by the report's day, and every row still open whenever it falls due.
  const rows = f.filter((s) => (s.due !== null && s.due <= i.day) || s.state !== 'done');
  const open = rows.filter((s) => s.state !== 'done');
  return {
    head: 'THE FINANCE YEAR',
    cols: '1fr 120px',
    rows: [
      [`Rows due by ${shortDay(i.day)}`, String(rows.length)],
      ['Done', String(rows.length - open.length)],
      ...(open.length
        ? [
            [
              `Not yet · ${open.map((s) => `${s.obligation} (${s.due ? shortDay(s.due) : 'no date yet'})`).join(' · ')}`,
              String(open.length),
            ],
          ]
        : []),
    ],
  };
}

export function yearEndPage(i: YearEndInput): PaperPage {
  const fy = String(i.fiscalYear);
  const y = i.year;
  return {
    title: `Year-end report · FY${fy}`,
    sub: `To the directors · ${longDay(i.day)} · from the finance director`,
    blocks: [
      {
        head: 'THE FOUR FIGURES',
        cols: '1fr 140px',
        rows: [
          ['Income', fig(y?.incomeCents)],
          ['Expenses', fig(y?.festivalExpensesCents)],
          ['Assessment + disbursements', y ? y.aodCents + y.distributedCents : NO_DATA],
          ['Retained', fig(y?.retainedCents)],
        ],
      },
      distributions(i),
      projects(i),
      financeYear(i),
    ],
    sign: [`${i.financeDirector ?? ''} · finance director`, 'Received · director'],
    foot: 'Year-end report · page 1 of 1',
  };
}
